/* Generated from pricing/core.mjs and pricing/theme-ui.js. */
(()=>{
// Shopify theme money is in hundredths. Preserve six decimal places for unit add-ons.
function quantity(value) {
  const text = String(value).trim();
  if (!/^(?:[1-9]\d*|[1-9]\d{0,2}(?:,\d{3})+)$/.test(text)) throw new Error('Invalid quantity');
  const n = Number(text.replaceAll(',', ''));
  if (!Number.isSafeInteger(n)) throw new Error('Quantity is too large');
  return n;
}
function micros(value) {
  const text = String(value).trim();
  if (!/^\d{1,9}(?:\.\d{1,6})?$/.test(text)) throw new Error('Price must be non-negative with at most nine integer and six decimal digits');
  const [whole, fraction = ''] = text.split('.');
  return BigInt(whole) * 1000000n + BigInt(fraction.padEnd(6, '0'));
}
function quote({ baseCents, pieces, referenceCents, referencePieces, addons = [] }) {
  for (const n of [baseCents, pieces, referenceCents, referencePieces]) {
    if (!Number.isSafeInteger(n) || n < 0) throw new Error('Invalid pricing input');
  }
  if (!pieces || !referencePieces) throw new Error('Quantity must be positive');
  const savings = referenceCents ? Math.round((1 - (baseCents * referencePieces) / (referenceCents * pieces)) * 100) : 0;
  const factor = Math.max(0, 100 - savings);
  const sum = addons.reduce((total, price) => total + micros(price), 0n);
  // Round once, after summing and multiplying the entire batch; half-up to cents.
  const numerator = sum * BigInt(pieces) * BigInt(factor);
  const extraCents = Number((numerator + 500000n) / 1000000n);
  const totalCents = baseCents + extraCents;
  if (!Number.isSafeInteger(totalCents)) throw new Error('Price is too large');
  return { savings, extraCents, totalCents, unitCents: totalCents / pieces };
}
function validateOptions(options) {
  if (!Array.isArray(options) || options.length > 30) throw new Error('Expected up to 30 option groups');
  const labels = new Set();
  return options.map((group, index) => {
    const label = String(group.label || '').trim();
    if (!label || label.length > 100 || /[\[\]\r\n]/.test(label) || labels.has(label)) throw new Error('Option labels must be unique and valid');
    labels.add(label);
    if (!Array.isArray(group.values) || !group.values.length || group.values.length > 100) throw new Error('Each group needs 1–100 values');
    const names = new Set();
    return { id: String(group.id || `group-${index}`), label, values: group.values.map((value, i) => {
      const name = String(value.name || '').trim();
      if (!name || name.length > 150 || names.has(name)) throw new Error('Value names must be unique and non-empty');
      names.add(name);
      micros(value.price);
      const color = String(value.color || '');
      if (color && !/^#[a-f\d]{6}$/i.test(color)) throw new Error('Invalid color');
      const image = String(value.image || '');
      if (image && !/^https:\/\/(?:cdn\.shopify\.com\/|restopack\.com\/cdn\/shop\/files\/)/.test(image)) throw new Error('Select an image hosted in Shopify Files');
      return { id: String(value.id || `value-${i}`), name, price: String(value.price), image, color };
    }) };
  });
}

class RestopackPricing extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.form = this.closest('form');
    this.info = this.closest('product-info');
    this.error = this.querySelector('[data-pricing-error]');
    this.ready = false;
    this.form.addEventListener('submit', e => {
      if(this.initialized)this.update();
      if (!this.ready) {e.preventDefault();e.stopImmediatePropagation();this.error.focus();}
    }, {capture:true,signal:this.abort.signal});
    try {
      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.options = validateOptions(JSON.parse(this.querySelector('[data-options-json]').textContent));
      this.selected = this.product.variants.find(v => String(v.id) === this.form.elements.id.value)?.options.slice() || this.product.variants[0].options.slice();
      this.qtyIndex = Number(this.querySelector('.custom-option-quantity')?.dataset.optionIndex);
      if (!Number.isInteger(this.qtyIndex)) throw new Error('Quantity option is missing');
      this.renderExtras();
      this.renderMobileSelectors();
      this.addEventListener('click', e => this.select(e), {signal:this.abort.signal});
      this.addEventListener('change', e => {
        if(!e.target.matches('[data-mobile-choice]'))return;
        const block=e.target.closest('.rp-has-mobile-choice');
        const row=block?.querySelectorAll('.custom-option-list > .custom-option-item')[Number(e.target.value)];
        if(row)this.select({target:row});
      }, {signal:this.abort.signal});
      this.addEventListener('keydown', e => {
        if (['Enter',' '].includes(e.key) && e.target.matches('li.custom-option-item')) {e.preventDefault();e.target.click();}
      }, {signal:this.abort.signal});
      this.querySelectorAll('li.custom-option-item').forEach(el => {el.tabIndex=0;el.setAttribute('role','button');});
      this.form.elements.id.addEventListener('change', () => {
        const v=this.product.variants.find(v=>String(v.id)===this.form.elements.id.value);
        if(v) this.selected=v.options.slice();
        this.update();
      }, {signal:this.abort.signal});
      // The existing store uses YMQ's .product-additional field, which can load late.
      this.initialized=true;
      this.timer=setInterval(()=>this.update(),1000);
      this.update();
    } catch(e) {this.fail(e.message);}
  }
  disconnectedCallback() { this.abort?.abort();this.abort=null;clearInterval(this.timer); }
  fail(message,buttonLabel='Pricing unavailable') {
    this.ready=false;this.error.hidden=false;this.error.textContent=message;
    const submit=this.form?.querySelector('[type=submit][name=add]');
    submit?.removeAttribute('aria-busy');
    if(submit&&!submit.disabled){submit.dataset.pricingBlocked='true';submit.disabled=true;}
    const label=submit?.querySelector('span');
    if(label){if(!submit.dataset.pricingLabel)submit.dataset.pricingLabel=submit.dataset.addToCartLabel||label.textContent.trim();label.textContent=buttonLabel;}
    const activeRow=this.querySelector('.custom-option-quantity .is-active:not(.disabled)');
    const status=activeRow?.querySelector('[data-quantity-status]');
    if(status){status.textContent=buttonLabel;status.hidden=false;activeRow.title=message;}
  }
  pendingSelection() {
    this.ready=false;this.error.hidden=true;
    const submit=this.form.querySelector('[type=submit][name=add]');
    if(!submit)return;
    const label=submit.querySelector('span');
    if(!submit.dataset.pricingLabel)submit.dataset.pricingLabel=submit.dataset.addToCartLabel||label.textContent.trim();
    submit.dataset.pricingBlocked='true';submit.disabled=true;submit.setAttribute('aria-busy','true');
    label.textContent='Updating…';
  }
  renderExtras() {
    const host=this.querySelector('[data-extra-options]');
    this.options.forEach((group,index)=>{
      const div=document.createElement('div');div.className='extra-option-dropdown';div.dataset.group=index;
      const label=document.createElement('div');label.className='custom-option-toggle';
      const title=document.createElement('span');title.className='custom-option-label';title.textContent=group.label;
      label.append(title);div.append(label);
      const ul=document.createElement('ul');ul.className='custom-option-list';
      group.values.forEach((value,i)=>{
        const li=document.createElement('li');li.className='custom-option-item'+(i===0?' is-active':'');li.dataset.valueIndex=i;
        if(value.image){const img=document.createElement('img');img.src=value.image;img.alt='';img.width=50;img.height=50;img.loading='lazy';li.append(img);}
        else if(value.color){const swatch=document.createElement('span');swatch.className='extra-variant-color';swatch.style.backgroundColor=value.color;li.append(swatch);}
        const text=document.createElement('span');text.textContent=value.name;li.append(text);ul.append(li);
      });
      const input=document.createElement('input');input.type='hidden';input.name=`properties[${group.label}]`;input.dataset.optionProperty=index;
      div.append(ul,input);host.append(div);
    });
  }
  select(event) {
    const row=event.target.closest('.custom-option-item');if(!row||!this.contains(row)||row.classList.contains('disabled'))return;
    const group=row.closest('[data-group]');
    if(group){group.querySelectorAll('.custom-option-item').forEach(item=>item.classList.toggle('is-active',item===row));this.update();return;}
    const block=row.closest('[data-option-index]');if(!block)return;
    const index=Number(block.dataset.optionIndex),value=row.dataset.value;
    const native=this.info?.querySelectorAll('variant-selects .product-form__input')[index];
    const radio=[...(native?.querySelectorAll('input[type=radio]')||[])].find(input=>input.value===value);
    const select=native?.querySelector('select');
    if(!radio&&!select){this.fail('This option is unavailable. Please reload the page.');return;}
    this.selected[index]=value;this.ready=false;this.selectionStarted=Date.now();
    if(radio){radio.checked=true;radio.dispatchEvent(new Event('change',{bubbles:true}));}
    else{select.value=value;select.dispatchEvent(new Event('change',{bubbles:true}));}
    this.update();
  }
  renderMobileSelectors() {
    this.querySelectorAll('.custom-option-dropdown,.extra-option-dropdown').forEach((block,index)=>{
      if(block.querySelector('[data-mobile-choice]'))return;
      const wrapper=document.createElement('div');wrapper.className='rp-mobile-choice';
      const label=document.createElement('label');label.textContent=block.querySelector('.custom-option-label').textContent;
      const select=document.createElement('select');select.dataset.mobileChoice='';
      select.id=`${this.form.id || this.info.id}-mobile-choice-${index}`;label.htmlFor=select.id;
      block.querySelectorAll('.custom-option-list > .custom-option-item').forEach((row,i)=>{
        const option=document.createElement('option');option.value=String(i);option.textContent=row.textContent.trim();select.append(option);
      });
      wrapper.append(label,select);block.append(wrapper);block.classList.add('rp-has-mobile-choice');
    });
  }
  syncMobileSelectors() {
    this.querySelectorAll('.rp-has-mobile-choice').forEach(block=>{
      const select=block.querySelector('[data-mobile-choice]');
      let active=null;
      block.querySelectorAll('.custom-option-list > .custom-option-item').forEach((row,i)=>{
        const option=select.options[i];if(row.classList.contains('is-active'))active=option;
        option.disabled=row.classList.contains('disabled');
      });
      if(active&&!active.selected)active.selected=true;
    });
  }
  syncYmq(extraCents) {
    // YMQ also renders a hidden line-item property input in this wrapper.
    // Only its text input accepts the add-on price and change event.
    const fields=this.info?.querySelectorAll('.product-additional input[type="text"]');
    if(!fields?.length) return extraCents===0 && !this.options.some(g=>g.values.some(v=>micros(v.price)>0n));
    if(fields.length!==1) return false;
    const field=fields[0];
    try {
      // The installed YMQ build uses a global lexical `let`, which is not
      // exposed on window. Keep compatibility with builds using window too.
      const jq=typeof ymqJq==='function'?ymqJq:window.ymqJq;
      if(typeof jq!=='function') return false;
      const target=jq(field);
      if(this.lastField!==field || this.lastExtra!==extraCents){
        target.data('price',extraCents/100).change();
        this.lastField=field;this.lastExtra=extraCents;
      }
      return Number(target.data('price'))===extraCents/100;
    }catch{return false;}
  }
  update() {
    if(!this.isConnected||!this.product)return;
    try {
      const addons=this.options.map((group,i)=>{
        const block=this.querySelector(`[data-group="${i}"]`);
        const chosen=group.values[Number(block.querySelector('.is-active').dataset.valueIndex)];
        block.querySelector('input').value=chosen.name;return chosen.price;
      });
      if(this.dataset.currency!=='USD'&&addons.some(price=>micros(price)>0n))throw new Error('Custom pricing is currently available in USD. Please select USD to continue.');
      const variants=this.product.variants.filter(v=>v.options.every((o,i)=>i===this.qtyIndex||o===this.selected[i]));
      const sorted=variants.slice().sort((a,b)=>quantity(a.options[this.qtyIndex])-quantity(b.options[this.qtyIndex]));
      const reference=sorted[0];let active=null;
      const money=new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:this.dataset.currency});
      this.querySelectorAll('[data-option-index]').forEach(block=>{
        const index=Number(block.dataset.optionIndex);
        block.querySelectorAll('.custom-option-item').forEach(row=>{row.classList.toggle('is-active',row.dataset.value===this.selected[index]);row.setAttribute('aria-pressed',String(row.dataset.value===this.selected[index]));});
        const label=block.querySelector('.custom-option-current');if(label)label.textContent=this.selected[index];
      });
      this.querySelectorAll('.custom-option-quantity .custom-option-item').forEach(row=>{
        const v=variants.find(v=>v.options[this.qtyIndex]===row.dataset.value);
        const unavailable=!v?'Not available':!v.available?'Sold out':'';
        row.classList.toggle('disabled',!!unavailable);row.setAttribute('aria-disabled',String(!!unavailable));row.tabIndex=unavailable?-1:0;
        let status=row.querySelector('[data-quantity-status]');
        if(!status){status=document.createElement('small');status.dataset.quantityStatus='';row.querySelector('.swatchs-label__Quantity').append(status);}
        if(status.textContent!==unavailable)status.textContent=unavailable;
        status.hidden=!unavailable;row.title=unavailable?`${row.dataset.value}: ${unavailable} for this selection`:'';
        const result=v?quote({baseCents:v.price,pieces:quantity(row.dataset.value),referenceCents:reference.price,referencePieces:quantity(reference.options[this.qtyIndex]),addons}):null;
        const texts={total:result?money.format(result.totalCents/100):'—',unit:result?money.format(result.unitCents/100)+' / Unit':'—',sale:result?`${result.savings}%`:'—'};
        for(const [role,text] of Object.entries(texts)){const el=row.querySelector(`[data-role=${role}]`);if(el.textContent!==text)el.textContent=text;}
        if(v&&row.dataset.value===this.selected[this.qtyIndex])active={v,...result};
      });
      if(!active?.v.available){
        this.querySelector('.swatche-total-right').textContent='—';
        throw Object.assign(new Error(active?'This quantity is sold out. Please choose an available quantity.':'This quantity is not available for the selected specifications. Please choose another quantity or size.'),{buttonLabel:active?'Sold out':'Not available'});
      }
      this.querySelector('.swatche-total-right').textContent=money.format(active.totalCents/100);
      const cartQuantity=this.form.querySelector('[name=quantity]');
      if(cartQuantity&&Number(cartQuantity.value)!==1)throw new Error('Please add one configured batch at a time.');
      if(String(active.v.id)!==this.form.elements.id.value){
        if(Date.now()-this.selectionStarted<5000){this.pendingSelection();return;}
        throw Object.assign(new Error('Your selection could not be updated yet. Please try again or reload the page.'),{buttonLabel:'Selection unavailable'});
      }
      if(!this.syncYmq(active.extraCents))throw Object.assign(new Error('The price for your selection is not ready. Please reload the page or contact us for help.'),{buttonLabel:'Pricing unavailable'});
      this.error.hidden=true;this.ready=true;
      const submit=this.form.querySelector('[data-pricing-blocked]');
      if(submit){delete submit.dataset.pricingBlocked;submit.disabled=false;submit.removeAttribute('aria-busy');}
      const labeled=this.form.querySelector('[data-pricing-label]');
      if(labeled&&!labeled.disabled){labeled.querySelector('span').textContent=labeled.dataset.pricingLabel;delete labeled.dataset.pricingLabel;}
    }catch(e){this.fail(e.message,e.buttonLabel);}
    finally{this.syncMobileSelectors();}
  }
}
if(!customElements.get('rp-custom-pricing'))customElements.define('rp-custom-pricing',RestopackPricing);

})();
